// src/services/user.api.ts

import apiConfig from 'http/services/api.config'
import apiService from 'http/services/api.service'
import { ApiResponse } from 'http/types/api-response'
import { User } from 'domain/user'
import {
  CreateUserDto,
  UpdateUserDto,
  UserQueryParams,
} from '../types/user.types'

class UserApiService {
  private readonly baseUrl: string

  constructor() {
    this.baseUrl = apiConfig.endpoints.users
  }

  /**
   * Get all users with pagination and filtering
   */
  public async getUsers(
    params?: UserQueryParams
  ): Promise<ApiResponse<User[]>> {
    return apiService.get<ApiResponse<User[]>>(
      this.baseUrl + `/${params?.clientId}`
    )
  }

  /**
   * Get user by ID
   */
  public async getUserById(id: string): Promise<ApiResponse<User>> {
    return apiService.get<ApiResponse<User>>(`${this.baseUrl}/${id}`)
  }

  /**
   * Create a new user
   */
  public async createUser(
    userData: CreateUserDto,
    clientId: string
  ): Promise<ApiResponse<User>> {
    return apiService.post<ApiResponse<User>>(
      `${this.baseUrl}/${clientId}`,
      userData
    )
  }

  /**
   * Update an existing user
   */
  public async updateUser(
    id: string,
    userData: UpdateUserDto
  ): Promise<ApiResponse<User>> {
    return apiService.put<ApiResponse<User>>(`${this.baseUrl}/${id}`, userData)
  }

  /**
   * Delete a user
   */
  public async deleteUser(id: string): Promise<void> {
    return apiService.delete<void>(`${this.baseUrl}/${id}`)
  }

  /**
   * Reset user password
   */
  public async resetPassword(
    id: string,
    newPassword: string
  ): Promise<ApiResponse<User>> {
    return apiService.patch<ApiResponse<User>>(
      `${this.baseUrl}/${id}/reset-password`,
      {
        password: newPassword,
      }
    )
  }

  /**
   * Update user's last login timestamp
   */
  public async updateLastLogin(id: string): Promise<ApiResponse<User>> {
    return apiService.patch<ApiResponse<User>>(
      `${this.baseUrl}/${id}/last-login`,
      {
        lastLogin: new Date(),
      }
    )
  }
}

export const userApiService = new UserApiService()
export default userApiService
